import { combineReducers, applyMiddleware, createStore } from 'redux';
import thunk from 'redux-thunk';
import { type ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { wixDataReducer } from '@wix/communities-blog-client-common';
import { type PlatformApi } from '@app/external/common/controller/platform-api';
import { postMetadataMiddleware } from '../../common/middleware/post-metadata-middleware';
import commonReducers from '../../common/reducers';
import { getCurrentSiteLanguage } from '../../common/services/get-current-site-language';
import getEnvironment from '../../common/services/get-environment';
import categories from '../../common/store/categories/categories-reducer';
import postCount from '../../common/store/post-count/post-count-reducer';
import { type AppStore, type ControllerConfig } from '../../common/types';
import { type PostListWidgetThunkArgs } from '../types/post-list-widget-thunk-args';

interface CreateReduxStoreParams {
  controllerConfig: ControllerConfig;
  platformApi: PlatformApi;
  flowAPI: ControllerFlowAPI;
}

export function createReduxStore({
  controllerConfig,
  platformApi,
  flowAPI,
}: CreateReduxStoreParams) {
  const { appParams, compId } = controllerConfig;
  const { isSSR } = getEnvironment(platformApi);
  const language = getCurrentSiteLanguage(platformApi, flowAPI);

  const middleware = [
    thunk.withExtraArgument({
      platformApi,
      compId,
      appParams,
      panoramaClient: flowAPI.panoramaClient,
      httpClient: flowAPI.httpClient,
      errorHandler: flowAPI.errorHandler,
      flowAPI,
    } satisfies PostListWidgetThunkArgs),
    postMetadataMiddleware({ isSSR, language, localizePosts: true }),
  ];

  const reducers = combineReducers({
    ...commonReducers,
    wixData: wixDataReducer,
    postCount,
    categories,
  });

  const initialState = { experiments: flowAPI.essentials.experiments.all() };

  return createStore(reducers, initialState, applyMiddleware(...middleware)) as unknown as AppStore;
}
